import { ROLES } from 'components/rbac/type';
import React, { ReactElement } from 'react';
import { Route, Navigate } from 'react-router-dom';
import Page from 'routes/page';
import { AUTH } from 'state/auth/type';
import views, { IView } from 'views';
import NotAuthorizedError from 'views/error/notauthorized';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import routeConfig from 'routes/config';

/**
 * Given a list of user roles and a list of page roles, return true if the user has at least one of the
 * page roles
 * @param {AUTH.IRole[]} userRoles - AUTH.IRole[] = [],
 * @param {ROLES[]} pageConfig - ROLES[]
 * @param {string} orgId - The organization ID of the user.
 */
export const hasCommon = (
  pageConfig: ROLES[],
  orgId: string,
  userRoles: AUTH.IRole[] = []
) => {
  return userRoles
    .filter((item: AUTH.IRole) => item.tenant_id === orgId)
    .some((item: AUTH.IRole) => pageConfig.includes(item.role));
};

/**
 * Given a layout, return the views that are associated with that layout
 * @param {string} layout - The layout name.
 */
export const getConfig = (
  layout: string,
  view = views.dispensary
): IView[] => view.filter((prop: IView) => prop.layout === layout);

/**
 * It renders a page if the user is authorized to view it
 * @param {IView} child - IView - this is the child object that is passed in from the parent.
 * @param {boolean} isAuthorized - boolean - This is a boolean value that determines whether the user
 * is authorized to view the page.
 * @returns A Route component that will render a Page component if the user is authorized.
 */
export const renderPage = (child: IView, isAuthorized: boolean) => {
  return child.component ? (
    <Route
      path={child.path}
      element={
        isAuthorized ? (
          <Page title={`${child.title} - TreezPay`}>{child.component}</Page>
        ) : (
          <NotAuthorizedError />
        )
      }
    />
  ) : null;
};

export const checkPathAuthorization = (
  view: IView,
  permissions?: PAYMENT_CONFIG.IEntityPermissionObject
): boolean => {
  if (!view.requiredPermissions) {
    return true;
  }

  return Boolean(
    permissions?.permissions?.length &&
      view.requiredPermissions.every((requiredPermission) =>
        permissions.permissions.includes(requiredPermission)
      )
  );
};

export const checkPermissionForPermissionString = (
  permissionString: string, 
  permissions?: PAYMENT_CONFIG.IEntityPermissionObject
): boolean => {

  return Boolean(
    permissions?.permissions?.length &&
    permissions.permissions.includes(permissionString)
  );
}

/*
 * Get routes for specified layout's config object
 */
export const getRoutes = (
  config: IView[] = [],
  permissions?: PAYMENT_CONFIG.IEntityPermissionObject
): ReactElement<string, string>[] | null => {
  return config.map((view: IView) => {
    return view.children && view.children.length > 0 ? (
      <>
        {view.children.map((child) => {
          const isAuthorized = checkPathAuthorization(child, permissions);
          return renderPage(child, isAuthorized);
        })}
        <Route path="*" element={<Navigate to={view.path} replace />} />
      </>
    ) : (
      <>
        <Route
          path={view.path}
          element={
            view.component ? (
              <Page title={`${view.title} - TreezPay`}>{view.component}</Page>
            ) : (
              <NotAuthorizedError />
            )
          }
        />
        <Route path="*" element={<Navigate to={view.path} replace />} />
      </>
    );
  });
};
